import React, { useState, useEffect } from 'react';
import { TareaConRelaciones, TIPO_TAREA_LABELS, PRIORIDAD_LABELS, PRIORIDAD_COLORS, ESTADO_LABELS, EstadoTarea, Prioridad, Tamanio, ClaseServicio, TAMANIO_LABELS, CLASE_SERVICIO_LABELS, CLASE_SERVICIO_ICONS, DocumentoCliente, AudioCliente, ApiResponse } from '../types';
import { useKanban } from '../context/KanbanContext';
import { toast } from '../lib/toast';
import { apiFetch } from '../lib/apiFetch';
import { AudioDetalleModal } from './AudioDetalleModal';

const API_BASE_URL_AUDIOS = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

interface ModalEditarTareaProps {
    tarea: TareaConRelaciones;
    onClose: () => void;
    onActualizar: (tareaId: number, datos: { titulo?: string; descripcion?: string | null; prioridad?: Prioridad; fecha_limite?: string | null; responsable?: string | null; tamanio?: Tamanio; clase_servicio?: ClaseServicio; bloqueado?: boolean; motivo_bloqueo?: string | null }) => Promise<boolean>;
    onEliminar: (tareaId: number) => Promise<boolean>;
    onMarcarCompletado: (tareaId: number) => Promise<boolean>;
}

const tipoIcons: Record<string, string> = {
    bug_soporte: '🐛',
    nuevo_modulo: '🚀',
    actualizacion_refactor: '🔄'
};

const estadoColors: Record<EstadoTarea, string> = {
    backlog: 'bg-slate-400',
    por_hacer: 'bg-blue-500',
    en_progreso: 'bg-amber-500',
    esperando_cliente: 'bg-orange-500',
    testing: 'bg-purple-500',
    produccion: 'bg-emerald-500'
};

export const ModalEditarTarea: React.FC<ModalEditarTareaProps> = ({
    tarea,
    onClose,
    onActualizar,
    onEliminar,
    onMarcarCompletado
}) => {
    const { fetchKanbanBoard } = useKanban();
    const [editando, setEditando] = useState(false);
    const [eliminando, setEliminando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    // Tarea creada desde "Aprobar" en Audios trae audio_origen_id — permite
    // volver al audio que la originó sin tener que buscarlo a mano en la
    // pestaña Audios.
    const [audioOrigen, setAudioOrigen] = useState<AudioCliente | null>(null);
    const [cargandoAudio, setCargandoAudio] = useState(false);
    const [formData, setFormData] = useState({
        titulo: tarea.titulo,
        descripcion: tarea.descripcion || '',
        prioridad: tarea.prioridad as Prioridad,
        fecha_limite: tarea.fecha_limite ? (tarea.fecha_limite as string).split('T')[0] : '',
        responsable: tarea.responsable || '',
        tamanio: tarea.tamanio as Tamanio,
        clase_servicio: tarea.clase_servicio as ClaseServicio,
        bloqueado: tarea.bloqueado,
        motivo_bloqueo: tarea.motivo_bloqueo || ''
    });

    useEffect(() => {
        setFormData({
            titulo: tarea.titulo,
            descripcion: tarea.descripcion || '',
            prioridad: tarea.prioridad,
            fecha_limite: tarea.fecha_limite ? (tarea.fecha_limite as string).split('T')[0] : '',
            responsable: tarea.responsable || '',
            tamanio: tarea.tamanio,
            clase_servicio: tarea.clase_servicio,
            bloqueado: tarea.bloqueado,
            motivo_bloqueo: tarea.motivo_bloqueo || ''
        });
    }, [tarea]);

    const handleGuardar = async () => {
        setGuardando(true);
        const success = await onActualizar(tarea.id, {
            titulo: formData.titulo,
            descripcion: formData.descripcion || null,
            prioridad: formData.prioridad,
            fecha_limite: formData.fecha_limite || null,
            responsable: formData.responsable || null,
            tamanio: formData.tamanio,
            clase_servicio: formData.clase_servicio,
            bloqueado: formData.bloqueado,
            motivo_bloqueo: formData.bloqueado ? (formData.motivo_bloqueo || null) : null
        });
        setGuardando(false);
        if (success) {
            setEditando(false);
        }
    };

    const handleEliminar = async () => {
        const ok = await toast.confirm({
            title: '¿Eliminar esta tarea?',
            description: 'Esta acción no se puede deshacer.',
            confirmText: 'Eliminar',
            type: 'warning'
        });
        if (!ok) return;
        setEliminando(true);
        const success = await onEliminar(tarea.id);
        if (success) {
            onClose();
        } else {
            setEliminando(false);
        }
    };

    const handleMarcarCompletado = async () => {
        await onMarcarCompletado(tarea.id);
        onClose();
    };

    const handleVerAudio = async () => {
        if (!tarea.audio_origen_id) return;
        setCargandoAudio(true);
        try {
            const res = await apiFetch(`${API_BASE_URL_AUDIOS}/audios/${tarea.audio_origen_id}`);
            const json: ApiResponse<AudioCliente> = await res.json();
            if (json.success && json.data) {
                setAudioOrigen(json.data);
            } else {
                toast.error('No se pudo cargar el audio de origen');
            }
        } catch (e) {
            toast.error('No se pudo cargar el audio de origen');
        }
        setCargandoAudio(false);
    };

    const puedeMarcarCompletado = tarea.estado !== 'produccion';
    const esCompletada = tarea.estado === 'produccion';

    return (
        <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg"
                            style={{ backgroundColor: tarea.cliente_color }}
                        >
                            {tipoIcons[tarea.tipo_tarea]}
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">
                                {editando ? 'Editar Tarea' : 'Detalle de Tarea'}
                            </h2>
                            <p className="text-xs text-slate-500">{tarea.empresa} • {tarea.nombre_proyecto}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 space-y-5 overflow-y-auto flex-1 min-h-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full text-white ${estadoColors[tarea.estado]}`}>
                            {ESTADO_LABELS[tarea.estado]}
                        </span>
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                            {TIPO_TAREA_LABELS[tarea.tipo_tarea]}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${PRIORIDAD_COLORS[tarea.prioridad as Prioridad].bg} ${PRIORIDAD_COLORS[tarea.prioridad as Prioridad].text}`}>
                            {PRIORIDAD_COLORS[tarea.prioridad as Prioridad].icon} {PRIORIDAD_LABELS[tarea.prioridad as Prioridad]}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                            tarea.tamanio === 'XL' ? 'bg-red-100 text-red-700 ring-1 ring-red-300' : 'bg-slate-100 text-slate-600'
                        }`}>
                            Tamaño {TAMANIO_LABELS[tarea.tamanio]}
                        </span>
                        {tarea.clase_servicio !== 'estandar' && (
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                                tarea.clase_servicio === 'expedito' ? 'bg-red-500 text-white' : 'bg-indigo-100 text-indigo-700'
                            }`}>
                                {CLASE_SERVICIO_ICONS[tarea.clase_servicio]} {CLASE_SERVICIO_LABELS[tarea.clase_servicio]}
                            </span>
                        )}
                        {tarea.responsable && (
                            <span className="px-3 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                                👤 {tarea.responsable}
                            </span>
                        )}
                        {tarea.bloqueado && (
                            <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 ring-1 ring-red-300">
                                🚩 Bloqueado
                            </span>
                        )}
                        {tarea.audio_origen_id && (
                            <button
                                onClick={handleVerAudio}
                                disabled={cargandoAudio}
                                className="px-3 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors disabled:opacity-50"
                                title="Esta tarea se creó a partir de un audio — ver su detalle"
                            >
                                🎙️ {cargandoAudio ? 'Cargando audio...' : 'Ver audio de origen'}
                            </button>
                        )}
                    </div>

                    {tarea.bloqueado && tarea.motivo_bloqueo && !editando && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <p className="text-xs font-medium text-red-700 mb-1">🚩 Motivo del bloqueo</p>
                            <p className="text-sm text-red-800 whitespace-pre-wrap">{tarea.motivo_bloqueo}</p>
                        </div>
                    )}

                    {editando ? (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Título</label>
                                <input
                                    type="text"
                                    value={formData.titulo}
                                    onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                                    rows={4}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Prioridad</label>
                                    <select
                                        value={formData.prioridad}
                                        onChange={(e) => setFormData(prev => ({ ...prev, prioridad: e.target.value as Prioridad }))}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white"
                                    >
                                        {Object.entries(PRIORIDAD_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Fecha Límite</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_limite}
                                        onChange={(e) => setFormData(prev => ({ ...prev, fecha_limite: e.target.value }))}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Tamaño</label>
                                    <select
                                        value={formData.tamanio}
                                        onChange={(e) => setFormData(prev => ({ ...prev, tamanio: e.target.value as Tamanio }))}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white"
                                    >
                                        {Object.entries(TAMANIO_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                    {formData.tamanio === 'XL' && (
                                        <p className="text-xs text-red-600 mt-1">Considera dividir esta tarea en subtareas más pequeñas.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Clase de Servicio</label>
                                    <select
                                        value={formData.clase_servicio}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clase_servicio: e.target.value as ClaseServicio }))}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white"
                                    >
                                        {Object.entries(CLASE_SERVICIO_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{CLASE_SERVICIO_ICONS[value as ClaseServicio]} {label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Responsable</label>
                                <input
                                    type="text"
                                    value={formData.responsable}
                                    onChange={(e) => setFormData(prev => ({ ...prev, responsable: e.target.value }))}
                                    placeholder="Nombre de la persona asignada"
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.bloqueado}
                                        onChange={(e) => setFormData(prev => ({ ...prev, bloqueado: e.target.checked }))}
                                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                                    />
                                    <span className="text-sm font-medium text-slate-700">🚩 Marcar como bloqueada</span>
                                </label>
                                {formData.bloqueado && (
                                    <textarea
                                        value={formData.motivo_bloqueo}
                                        onChange={(e) => setFormData(prev => ({ ...prev, motivo_bloqueo: e.target.value }))}
                                        rows={2}
                                        placeholder="Motivo del bloqueo..."
                                        className="w-full mt-2 px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                                    />
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <h3 className="font-semibold text-slate-800 text-lg leading-snug">{tarea.titulo}</h3>
                            </div>

                            {tarea.descripcion && (
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{tarea.descripcion}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                {tarea.fecha_limite && (
                                    <div className="bg-slate-50 rounded-xl p-3">
                                        <span className="text-slate-500 text-xs">Fecha límite</span>
                                        <p className="font-medium text-slate-700">
                                            {new Date(tarea.fecha_limite).toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })}
                                        </p>
                                    </div>
                                )}
                                <div className="bg-slate-50 rounded-xl p-3">
                                    <span className="text-slate-500 text-xs">Creado</span>
                                    <p className="font-medium text-slate-700">
                                        {new Date(tarea.creado_en).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })}
                                    </p>
                                </div>
                            </div>

                            {esCompletada && (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-medium text-emerald-800">Tarea completada</p>
                                        <p className="text-xs text-emerald-600">Esta tarea fue marcada como terminada</p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <DocumentosTareaSection
                    tareaId={tarea.id}
                    clienteId={tarea.cliente_id}
                    empresa={tarea.empresa}
                    clienteColor={tarea.cliente_color}
                    onClose={onClose}
                />

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl flex-shrink-0">
                    <div className="flex flex-wrap gap-2">
                        {!editando && (
                            <>
                                <button
                                    onClick={() => setEditando(true)}
                                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Editar
                                </button>
                                {puedeMarcarCompletado && (
                                    <button
                                        onClick={handleMarcarCompletado}
                                        className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Completar
                                    </button>
                                )}
                                <button
                                    onClick={handleEliminar}
                                    disabled={eliminando}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    {eliminando ? 'Eliminando...' : 'Eliminar'}
                                </button>
                            </>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {editando && (
                            <>
                                <button
                                    onClick={() => setEditando(false)}
                                    className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleGuardar}
                                    disabled={guardando}
                                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
                                >
                                    {guardando ? (
                                        <>
                                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                            </svg>
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Guardar
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                        {!editando && (
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                            >
                                Cerrar
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>

        {/* Fuera del overlay de esta tarea, como sibling, no como hijo: si
            fuera hijo, el click en el propio backdrop del modal de audio
            burbujearía hasta el onClick={onClose} de este overlay y cerraría
            los dos modales de un solo click. */}
        {audioOrigen && (
            <AudioDetalleModal
                audio={audioOrigen}
                onClose={() => setAudioOrigen(null)}
                onActualizado={() => { handleVerAudio(); fetchKanbanBoard(); }}
            />
        )}
        </>
    );
};

interface DocumentosTareaSectionProps {
    tareaId: number;
    clienteId: number;
    empresa: string;
    clienteColor: string;
    onClose: () => void;
}

const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const DocumentosTareaSection: React.FC<DocumentosTareaSectionProps> = ({ tareaId, clienteId, empresa, clienteColor, onClose }) => {
    const { documentosPorTarea, fetchDocumentosTarea, subirDocumentoCliente, eliminarDocumento, fetchKanbanBoard } = useKanban();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!documentosPorTarea[tareaId]) {
            fetchDocumentosTarea(tareaId);
        }
    }, [tareaId, documentosPorTarea, fetchDocumentosTarea]);

    const docs = documentosPorTarea[tareaId] || [];

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setError(null);
        const result = await subirDocumentoCliente(clienteId, file, tareaId);
        setUploading(false);
        e.target.value = '';
        if (!result) {
            setError('No se pudo subir el documento');
            setTimeout(() => setError(null), 4000);
        }
    };

    const handleDelete = async (docId: number) => {
        const ok = await toast.confirm({
            title: '¿Eliminar este documento?',
            confirmText: 'Eliminar',
            type: 'warning'
        });
        if (!ok) return;
        await eliminarDocumento(docId);
    };

    const handleDownload = (docId: number) => {
        window.open(`${API_BASE_URL}/documentos/${docId}/descargar`, '_blank');
    };

    return (
        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/30 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    📎 Documentos adjuntos
                    <span className="text-xs font-normal text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
                        {docs.length}
                    </span>
                </h3>
                <label className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer ${
                    uploading
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {uploading ? 'Subiendo...' : 'Adjuntar'}
                    <input
                        type="file"
                        className="hidden"
                        disabled={uploading}
                        onChange={handleUpload}
                        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.zip,.xlsx,.xls,.csv"
                    />
                </label>
            </div>

            {error && (
                <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                </div>
            )}

            {docs.length === 0 ? (
                <p className="text-xs text-slate-400 italic py-2">
                    Sin documentos. Subí specs, screenshots o PDFs para tenerlos a mano.
                </p>
            ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {docs.map((doc: DocumentoCliente) => (
                        <div
                            key={doc.id}
                            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors group"
                        >
                            <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: clienteColor }}
                            />
                            <span className="text-xs text-slate-400 font-mono">#{doc.id}</span>
                            <button
                                onClick={() => handleDownload(doc.id)}
                                className="flex-1 text-left text-sm text-slate-700 hover:text-blue-600 truncate font-medium"
                                title={doc.nombre_original}
                            >
                                {doc.nombre_original}
                            </button>
                            <span className="text-[10px] text-slate-400 flex-shrink-0">
                                {formatBytes(doc.tamanio_bytes)}
                            </span>
                            <button
                                onClick={() => handleDelete(doc.id)}
                                className="text-slate-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                                title="Eliminar documento"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="mt-2 text-[10px] text-slate-400">
                Archivos del cliente <span className="font-medium" style={{ color: clienteColor }}>{empresa}</span> · máx 10MB
            </div>
        </div>
    );
};
